/**
 * Scheduling Service
 * 
 * This service handles workforce management, employee scheduling, 
 * appointments, and time tracking operations.
 */

import apiClient from './apiClient';

class SchedulingService {
    constructor() {
        this.baseURL = '/scheduling';
    }

    // ============================================================================
    // EMPLOYEE MANAGEMENT
    // ============================================================================

    /**
     * Create new employee record
     */
    async createEmployee(employeeData) {
        try {
            const response = await apiClient.post(`${this.baseURL}/employees`, employeeData);
            return {
                success: true,
                data: response.data,
                message: "Employee created successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to create employee",
                data: null
            };
        }
    }

    /**
     * Get list of employees with filtering
     */
    async getEmployees(params = {}) {
        try {
            const response = await apiClient.get(`${this.baseURL}/employees`, { params });
            return {
                success: true,
                data: response.data || [],
                message: "Employees fetched successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch employees",
                data: []
            };
        }
    }

    /**
     * Get employee by ID
     */
    async getEmployeeById(employeeId) {
        try {
            const response = await apiClient.get(`${this.baseURL}/employees/${employeeId}`);
            return {
                success: true,
                data: response.data,
                message: "Employee fetched successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch employee",
                data: null
            };
        }
    }

    /**
     * Update employee information
     */
    async updateEmployee(employeeId, employeeData) {
        try {
            const response = await apiClient.put(`${this.baseURL}/employees/${employeeId}`, employeeData);
            return {
                success: true,
                data: response.data,
                message: "Employee updated successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to update employee",
                data: null
            };
        }
    }

    // ============================================================================
    // SCHEDULE MANAGEMENT
    // ============================================================================

    /**
     * Create new schedule
     */
    async createSchedule(scheduleData) {
        try {
            const response = await apiClient.post(`${this.baseURL}/schedules`, scheduleData);
            return {
                success: true,
                data: response.data,
                message: "Schedule created successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to create schedule",
                data: null
            };
        }
    }

    /**
     * Get list of schedules with filtering
     */
    async getSchedules(params = {}) {
        try {
            const response = await apiClient.get(`${this.baseURL}/schedules`, { params });
            return {
                success: true,
                data: response.data || [],
                message: "Schedules fetched successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch schedules",
                data: []
            };
        }
    }

    /**
     * Get schedule by ID
     */
    async getScheduleById(scheduleId) {
        try {
            const response = await apiClient.get(`${this.baseURL}/schedules/${scheduleId}`);
            return {
                success: true,
                data: response.data,
                message: "Schedule fetched successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch schedule",
                data: null
            };
        }
    }

    /**
     * Update schedule
     */
    async updateSchedule(scheduleId, scheduleData) {
        try {
            const response = await apiClient.put(`${this.baseURL}/schedules/${scheduleId}`, scheduleData);
            return {
                success: true,
                data: response.data,
                message: "Schedule updated successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to update schedule",
                data: null
            };
        }
    }

    // ============================================================================
    // TIME TRACKING
    // ============================================================================

    /**
     * Clock in for a schedule
     */
    async clockIn(scheduleId, clockData = {}) {
        try {
            const response = await apiClient.post(`${this.baseURL}/schedules/${scheduleId}/clock-in`, clockData);
            return {
                success: true,
                data: response.data,
                message: "Clocked in successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to clock in",
                data: null
            };
        }
    }

    /**
     * Clock out for a schedule
     */
    async clockOut(scheduleId, clockData = {}) {
        try {
            const response = await apiClient.post(`${this.baseURL}/schedules/${scheduleId}/clock-out`, clockData);
            return {
                success: true,
                data: response.data,
                message: "Clocked out successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to clock out",
                data: null
            };
        }
    }

    // ============================================================================
    // APPOINTMENT MANAGEMENT
    // ============================================================================

    /**
     * Create new appointment
     */
    async createAppointment(appointmentData) {
        try {
            const response = await apiClient.post(`${this.baseURL}/appointments`, appointmentData);
            return {
                success: true,
                data: response.data,
                message: "Appointment created successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to create appointment",
                data: null
            };
        }
    }

    /**
     * Get list of appointments with filtering
     */
    async getAppointments(params = {}) {
        try {
            const response = await apiClient.get(`${this.baseURL}/appointments`, { params });
            return {
                success: true,
                data: response.data || [],
                message: "Appointments fetched successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch appointments",
                data: []
            };
        }
    }

    /**
     * Get appointment by ID
     */
    async getAppointmentById(appointmentId) {
        try {
            const response = await apiClient.get(`${this.baseURL}/appointments/${appointmentId}`);
            return {
                success: true,
                data: response.data,
                message: "Appointment fetched successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch appointment",
                data: null
            };
        }
    }

    /**
     * Update appointment
     */
    async updateAppointment(appointmentId, appointmentData) {
        try {
            const response = await apiClient.put(`${this.baseURL}/appointments/${appointmentId}`, appointmentData);
            return {
                success: true,
                data: response.data,
                message: "Appointment updated successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to update appointment",
                data: null
            };
        }
    }

    /**
     * Update appointment status
     */
    async updateAppointmentStatus(appointmentId, statusData) {
        try {
            const response = await apiClient.patch(`${this.baseURL}/appointments/${appointmentId}/status`, statusData);
            return {
                success: true,
                data: response.data,
                message: "Appointment status updated successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to update appointment status",
                data: null
            };
        }
    }

    // ============================================================================
    // PROVIDER SCHEDULES
    // ============================================================================

    /**
     * Create provider schedule
     */
    async createProviderSchedule(scheduleData) {
        try {
            const response = await apiClient.post(`${this.baseURL}/provider-schedules`, scheduleData);
            return {
                success: true,
                data: response.data,
                message: "Provider schedule created successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to create provider schedule",
                data: null
            };
        }
    }

    /**
     * Get provider schedules
     */
    async getProviderSchedules(params = {}) {
        try {
            const response = await apiClient.get(`${this.baseURL}/provider-schedules`, { params });
            return {
                success: true,
                data: response.data || [],
                message: "Provider schedules fetched successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch provider schedules",
                data: []
            };
        }
    }

    /**
     * Get provider schedule by ID
     */
    async getProviderScheduleById(scheduleId) {
        try {
            const response = await apiClient.get(`${this.baseURL}/provider-schedules/${scheduleId}`);
            return {
                success: true,
                data: response.data,
                message: "Provider schedule fetched successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch provider schedule",
                data: null
            };
        }
    }

    // ============================================================================
    // ANALYTICS AND METRICS
    // ============================================================================

    /**
     * Get workforce metrics
     */
    async getWorkforceMetrics(params = {}) {
        try {
            const response = await apiClient.get(`${this.baseURL}/analytics/workforce`, { params });
            return {
                success: true,
                data: response.data,
                message: "Workforce metrics fetched successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch workforce metrics",
                data: null
            };
        }
    }

    /**
     * Get scheduling metrics
     */
    async getSchedulingMetrics(params = {}) {
        try {
            const response = await apiClient.get(`${this.baseURL}/analytics/scheduling`, { params });
            return {
                success: true,
                data: response.data,
                message: "Scheduling metrics fetched successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch scheduling metrics",
                data: null
            };
        }
    }

    /**
     * Get dashboard metrics
     */
    async getDashboardMetrics(params = {}) {
        try {
            const response = await apiClient.get(`${this.baseURL}/analytics/dashboard`, { params });
            return {
                success: true,
                data: response.data,
                message: "Dashboard metrics fetched successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch dashboard metrics",
                data: null
            };
        }
    }

    // ============================================================================
    // UTILITY METHODS
    // ============================================================================

    /**
     * Check schedule conflicts
     */
    async checkScheduleConflicts(conflictData) {
        try {
            const response = await apiClient.post(`${this.baseURL}/schedules/check-conflicts`, conflictData);
            return {
                success: true,
                data: response.data,
                message: "Schedule conflict check completed"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to check schedule conflicts",
                data: null
            };
        }
    }

    /**
     * Search availability
     */
    async searchAvailability(searchData) {
        try {
            const response = await apiClient.post(`${this.baseURL}/availability/search`, searchData);
            return {
                success: true,
                data: response.data,
                message: "Availability search completed"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to search availability",
                data: null
            };
        }
    }

    // ============================================================================
    // ANALYTICS AND METRICS (Modern endpoints)
    // ============================================================================

    /**
     * Get staff duty schedule - redirects to modern schedules endpoint
     */
    async getStaffDuty(params = {}) {
        try {
            // Legacy method - redirect to modern schedules API
            return await this.getSchedules(params);
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch staff duty",
                data: []
            };
        }
    }
}

export default new SchedulingService(); 